import React, { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthUser, getCurrentUser, onAuthStateChange } from "../lib/auth";
import { supabase } from "../lib/supabase";

interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  error: string | null;
  remainingMessages: number | null;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isLoading: true,
  error: null,
  remainingMessages: null,
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [remainingMessages, setRemainingMessages] = useState<number | null>(
    null
  );
  const navigate = useNavigate();

  // Fetch remaining messages when user changes
  useEffect(() => {
    if (user) {
      const fetchRemainingMessages = async () => {
        try {
          // First check if the user has a subscription
          const { data, error } = await supabase
            .from("user_subscriptions")
            .select("*")
            .eq("user_id", user.id);

          if (error) {
            console.error("Error fetching user subscription:", error);
            setRemainingMessages(10); // Default value
            return;
          }

          // If user has a subscription, use it
          if (data && data.length > 0) {
            setRemainingMessages(data[0].remaining_messages);
          } else {
            // If no subscription exists, create one
            console.log("No subscription found, creating one...");

            try {
              const { data: newSubscription, error: insertError } =
                await supabase
                  .from("user_subscriptions")
                  .insert({
                    user_id: user.id,
                    remaining_messages: 10,
                    subscription_type: "free",
                    subscription_status: "active",
                  })
                  .select()
                  .single();

              if (insertError) {
                console.error("Error creating subscription:", insertError);
                setRemainingMessages(10); // Default value
              } else if (newSubscription) {
                console.log("Created new subscription:", newSubscription);
                setRemainingMessages(newSubscription.remaining_messages);
              } else {
                setRemainingMessages(10); // Default value
              }
            } catch (insertErr) {
              console.error("Failed to create subscription:", insertErr);
              setRemainingMessages(10); // Default value
            }
          }
        } catch (err) {
          console.error("Failed to fetch remaining messages:", err);
          setRemainingMessages(10); // Default value
        }
      };

      fetchRemainingMessages();
    } else {
      setRemainingMessages(null);
    }
  }, [user]);

  useEffect(() => {
    const initAuth = async () => {
      try {
        // Check for existing session
        const initialUser = await getCurrentUser();
        setUser(initialUser);
        setIsLoading(false);

        // Set up auth state change listener
        const {
          data: { subscription },
        } = onAuthStateChange((user) => {
          setUser(user);
          setIsLoading(false);
          setError(null);

          // Handle auth state changes
          if (user) {
            // If on callback page, navigate to generator
            if (window.location.pathname === "/auth/callback") {
              navigate("/generator", { replace: true });
            }
          } else {
            // If not on public routes and no user, redirect to sign in
            const publicRoutes = [
              "/",
              "/auth",
              "/auth/callback",
              "/pricing",
              "/contact",
              "/docs",
              "/generator",
            ];
            if (
              !publicRoutes.some((route) =>
                window.location.pathname.startsWith(route)
              )
            ) {
              navigate("/auth?mode=signin", { replace: true });
            }
          }
        });

        return () => {
          subscription.unsubscribe();
        };
      } catch (err) {
        console.error("Auth initialization error:", err);
        setError(
          err instanceof Error
            ? err.message
            : "Failed to initialize authentication"
        );
        setIsLoading(false);
      }
    };

    initAuth();
  }, [navigate]);

  return (
    <AuthContext.Provider value={{ user, isLoading, error, remainingMessages }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
